<template>
  <div>
    <b-field>
      <b-upload
        v-model="imageFile"
        @input="saveImageUrl"
        :class="isConcurrent ? 'uploadConcurrent' : 'uploadBase'"
        :disabled="disabled"
        drag-drop>
        <section :class="isConcurrent ? 'ma-10' : 'ma-30'" v-if="!imageSrc">
          <div class="level" :class="isConcurrent ? 'is-marginless pt-10' : ''">
            <div class="level-item">
              <div class="image is-32x32">
                <img src="@/assets/images/general/jpeg.png">
              </div>
            </div>
          </div>
          <div class="level" :class="isConcurrent ? 'is-marginless' : ''">
            <div class="level-item">
              <p class="is-size-7 has-text-default has-text-centered">
                {{$t('general.Text1DeLaDropdownImage')}}
                <br>
                {{$t('general.Text2DeLaDropdownImage')}}
              </p>
            </div>
          </div>
        </section>
        <section v-else class="image">
          <img id="img-pdv"
            :class="isConcurrent ? 'is-120x120' : ''"
            :src="imageSrc"
            @load="imageLoad">
        </section>
        <section class="loading-image" v-if="isLoad">
          <b-progress type="is-white" size="is-small"></b-progress>
        </section>
      </b-upload>
    </b-field>
    <b-modal :active.sync="activeModal" @close="closeModal" id="modal_cropper">
      <div class="has-background-white pa-40 is-relative">
        <b-loading v-model="isLoad" :is-full-page="false"></b-loading>
        <h4 class="is-size-6 has-text-weight-bold has-text-grid-1 mb-20">
          {{$t('general.modalCropper.title')}}
        </h4>
        <div class="columns">
          <div class="column is-8">
            <vue-cropper
              ref="cropper"
              :aspect-ratio="1/1"
              alt="Source Image"
              preview=".preview"
              :container-style="{
                height: '320px'
              }">
            </vue-cropper>
          </div>
          <div class="column">
            <div class="preview"></div>
            <p class="has-text-grid-2 is-size-7 has-text-centered">
              {{$t('general.modalCropper.preview')}}
            </p>
          </div>
        </div>
        <div class="buttons">
          <b-button
            type="is-primary"
            icon-left="magnify-minus"
            class="perfect-round"
            @click.prevent="zoom(-0.2)"/>
          <b-button
            type="is-primary"
            icon-left="magnify-plus"
            class="perfect-round"
            @click.prevent="zoom(0.2)"/>
        </div>
        <div class="buttons">
          <b-button
            type="is-transparent"
            class="has-text-grid-2"
            :disabled="isLoad"
            @click="closeModal">
            {{$t('general.modalCropper.btnAnnuler')}}
          </b-button>
          <b-button
            type="is-primary"
            class="has-text-weight-bold"
            :disabled="isLoad"
            @click="resizeImg">
            {{$t('general.modalCropper.btnAppliquer')}}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import VueCropper from 'vue-cropperjs';
import 'cropperjs/dist/cropper.css';
import gestionErreur from '@/services/general/gestionErreur';
import { saveLogo, getSrcLogoById } from '@/services/general/indexDbLogoService';
import { iconPointDeVente } from '@/services/gestionIcon/iconColorGetter';
import siteConfig from '@/configuration/siteConfig';
import { POINT_VENTE } from '@/configuration/clientConf';
import {
  TAILLE_PJ_LIMITE,
  DIMENSION_MAX_LOGO,
  QUALITE_JPEG_LOGO,
} from '../../configuration/pieceJointeConf';

export default {
  props: ['value', 'disabled', 'type', 'isConcurrent', 'itemId', 'saveToDb'],
  components: { VueCropper },
  data() {
    return {
      imageSrc: null,
      imageFile: null,
      inputMimeType: null,
      typeImgAccept: ['jpg', 'jpeg', 'png'],
      isLoad: false,
      activeModal: false,
    };
  },
  watch: {
    async itemId() {
      await this.initImageSrc();
    },
    async value() {
      await this.initImageSrc();
    },
  },
  computed: {
    useIndexDb() {
      return this.itemId && this.saveToDb;
    },
  },
  methods: {
    saveImageUrl (e) {
      this.imageFile = null;
      if (!e || !this.typeImgAccept.includes(e.type.split('/')[1])) {
        this.$emit('error', this.$t('general.messages.error.errorTypeFile'));
        return;
      }
      if (!e || TAILLE_PJ_LIMITE < e.size) {
        this.$emit('error', this.$t('general.messages.error.errorSizeFile', {
          fileName: e.name,
          maxSize: TAILLE_PJ_LIMITE / (1024 * 1024),
        }));
        return;
      }
      this.inputMimeType = e.type;
      this.activeModal = true;
      this.isLoad = true;
      this.$emit('fileChange', e);
      const reader = new FileReader();
      reader.readAsDataURL(e);
      reader.onload = (event) => {
        this.$refs.cropper.replace(event.target.result);
        this.isLoad = false;
      }
    },
    imageLoad() {
      if (this.type === POINT_VENTE) {
        const img = document.getElementById('img-pdv');
        if (img.src  && !img.src.includes('https://')) {
          const { widthIcon, colorIcon } = siteConfig.default.pdv;
          const dataUrlIcon = iconPointDeVente(img, widthIcon, colorIcon);
          this.$emit('dataUrlIcon', dataUrlIcon);
        }
      }
      this.isLoad = false;
    },
    canvasToBlob(canvas, type, quality) {
      return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
    },
    blobToDataUrl(blob) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    },
    async canvasToDataUrl(canvas) {
      const keepPng = this.inputMimeType === 'image/png';
      let blob = keepPng
        ? await this.canvasToBlob(canvas, 'image/png')
        : await this.canvasToBlob(canvas, 'image/jpeg', QUALITE_JPEG_LOGO);
      if (blob.size > TAILLE_PJ_LIMITE) {
        blob = await this.canvasToBlob(canvas, 'image/jpeg', QUALITE_JPEG_LOGO);
      }
      return this.blobToDataUrl(blob);
    },
    async resizeImg() {
      this.isLoad = true;
      await this.$nextTick();
      try {
        const canvas = this.$refs.cropper.getCroppedCanvas({
          maxWidth: DIMENSION_MAX_LOGO,
          maxHeight: DIMENSION_MAX_LOGO,
        });
        const dataUrl = await this.canvasToDataUrl(canvas);
        this.imageSrc = dataUrl;
        if (this.useIndexDb) {
          await saveLogo({ id: this.itemId, srcImg: this.imageSrc, heightImg: canvas.width });
        } else {
          this.$emit('input', this.imageSrc);
          this.$emit('heightImgChange', canvas.width);
        }
        this.closeModal();
      } catch (error) {
        gestionErreur('Erreur lors du redimensionnement de l\'image', error);
      } finally {
        this.isLoad = false;
      }
    },
    zoom(percent) {
      this.$refs.cropper.relativeZoom(percent);
    },
    closeModal() {
      this.imageFile = null;
      this.isLoad = false;
      this.activeModal = false;
    },
    async initImageSrc() {
      this.imageSrc = this.useIndexDb
        ? await getSrcLogoById(this.itemId, this.value)
        : this.value;
    }
  },
  async mounted() {
    await this.initImageSrc();
  },
};
</script>
